import React, { useMemo } from 'react';

import { ZonedDate } from '@eika-infrastruktur/date';

import type { AccountId, PaymentType } from '../../generated-types';
import useShowPaymentResult, { useShowTransactionResult } from '../../hooks/useShowPaymentResult';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import { PaymentRetryContext } from '../transaction/PaymentRetry';
import TransactionResultInfo from '../transaction/TransactionResultInfo';
import UpdateFuturePaymentRetry from '../transaction/UpdateFuturePaymentRetry';
import StopFuturePaymentTransactionResult from './StopFuturePaymentTransactionResult';
import UpdateFuturePaymentTransactionResult from './UpdateFuturePaymentTransactionResult';

type Props = {
    active?: boolean;
    onClose: () => void;
    status: 'updating' | 'payNow' | null;
    paymentType?: PaymentType;
    paymentDate?: ZonedDate;
    fromAccountId?: AccountId;
    children?: React.ReactNode;
};

function FuturePaymentSigningWrapper({
    active = true,
    children,
    fromAccountId,
    paymentType = 'PAYMENT',
    paymentDate,
    status,
    onClose,
}: Props) {
    const { signingError, signingStatus, handleCancelSigningFlow, showPaymentRetry } = useSigningContext();

    const showTransactionResult = useShowTransactionResult();
    const showPaymentResult = useShowPaymentResult();

    const showResult = useMemo(
        () =>
            paymentType === 'TRANSFER' || paymentType === 'RECURRING_TRANSFER'
                ? showTransactionResult
                : showPaymentResult,
        [showPaymentResult, showTransactionResult, paymentType],
    );

    if (!active) return <>{children}</>;

    if (showPaymentRetry && status === 'updating') {
        return <UpdateFuturePaymentRetry onClose={onClose} paymentType="TRANSFER" />;
    }

    if (showPaymentRetry && status === 'payNow') {
        return <PaymentRetryContext />;
    }

    if ((signingError || signingStatus === 'COMPLETED') && status === 'payNow') {
        return (
            <TransactionResultInfo
                type={paymentType}
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                fromAccountId={fromAccountId}
                onClose={onClose}
                paymentDate={paymentDate}
            />
        );
    }

    if (showResult && status === 'updating') {
        return (
            <UpdateFuturePaymentTransactionResult
                paymentType={paymentType}
                paymentStatus={signingStatus}
                paymentError={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                onClose={onClose}
            />
        );
    }

    if (showResult) {
        return (
            <StopFuturePaymentTransactionResult
                paymentType={paymentType}
                paymentStatus={signingStatus}
                paymentError={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                onClose={onClose}
            />
        );
    }

    return <SigningContextFlow onClosePayment={onClose}>{children}</SigningContextFlow>;
}

export default FuturePaymentSigningWrapper;
